import React, { useState, useEffect } from 'react';
import { 
  Target, Compass, Shield, Megaphone, Palette, Layers, Sparkles,
  BookOpen, PieChart, AlertTriangle, ChevronRight, Loader2, Edit3,
  X, Check, Eye
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const SECTION_ICONS = {
  brand_position: Target,
  brand_purpose: Compass,
  brand_promise: Shield,
  brand_pillars: Layers,
  brand_personality: Sparkles,
  brand_voice: Megaphone,
  visual_identity: Palette,
  content_series: BookOpen,
  content_mix: PieChart,
  guardrails: AlertTriangle
};

const SECTION_COLORS = {
  brand_position: 'from-blue-500/20 to-blue-600/20',
  brand_purpose: 'from-purple-500/20 to-purple-600/20',
  brand_promise: 'from-green-500/20 to-green-600/20',
  brand_pillars: 'from-orange-500/20 to-orange-600/20',
  brand_personality: 'from-pink-500/20 to-pink-600/20',
  brand_voice: 'from-cyan-500/20 to-cyan-600/20',
  visual_identity: 'from-yellow-500/20 to-yellow-600/20',
  content_series: 'from-indigo-500/20 to-indigo-600/20',
  content_mix: 'from-teal-500/20 to-teal-600/20',
  guardrails: 'from-red-500/20 to-red-600/20'
};

const AEBlueprint = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchBlueprint();
    }
  }, [userEmail]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchBlueprint = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/brand-blueprint?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setSections(data.data);
        if (data.data.length > 0 && !selectedSection) {
          setSelectedSection(data.data[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching blueprint:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSection = async () => {
    if (!selectedSection) return;
    
    try {
      const res = await fetch(
        `${API}/api/always-elevate/brand-blueprint/${selectedSection.id}?user_email=${encodeURIComponent(userEmail)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: editContent })
        }
      );
      const data = await res.json();
      if (data.success) {
        setEditMode(false);
        fetchBlueprint();
      }
    } catch (err) {
      console.error('Error saving section:', err);
    }
  };

  const startEdit = () => {
    setEditContent(selectedSection?.content || '');
    setEditMode(true);
  };

  const renderContent = (content) => {
    if (!content) return null;
    
    // Simple markdown-like rendering
    return content.split('\n').map((line, idx) => {
      // Bold text
      let processed = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-[#C4A052]">$1</strong>');
      // List items
      if (line.startsWith('- ')) {
        return (
          <div key={idx} className="flex gap-2 ml-2 my-1">
            <span className="text-[#C4A052]">•</span>
            <span dangerouslySetInnerHTML={{ __html: processed.substring(2) }} />
          </div>
        );
      }
      // Numbered items
      if (/^\d+\./.test(line)) {
        const num = line.match(/^(\d+)\./)[1];
        return (
          <div key={idx} className="flex gap-2 ml-2 my-1">
            <span className="text-[#C4A052] font-bold min-w-[20px]">{num}.</span>
            <span dangerouslySetInnerHTML={{ __html: processed.replace(/^\d+\.\s*/, '') }} />
          </div>
        );
      }
      // Regular line
      if (line.trim() === '') {
        return <div key={idx} className="h-3" />;
      }
      return (
        <p key={idx} className="my-1" dangerouslySetInnerHTML={{ __html: processed }} />
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-blueprint-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-blueprint-title">
                Digital Brand Blueprint
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                The foundation of the Always Elevate brand identity
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-[#C4A052]/10 border border-[#C4A052]/30 rounded-lg">
                <p className="text-xs text-gray-400">Version</p>
                <p className="text-[#C4A052] font-semibold">1.0</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar Navigation */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-black border border-white/20 rounded-xl overflow-hidden sticky top-8">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-semibold text-sm">Blueprint Sections</h3>
              </div>
              <div className="p-2">
                {sections.map((section) => {
                  const Icon = SECTION_ICONS[section.section] || Target;
                  const isSelected = selectedSection?.id === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => { setSelectedSection(section); setEditMode(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${
                        isSelected 
                          ? 'bg-[#C4A052]/20 text-[#C4A052]' 
                          : 'text-gray-400 hover:bg-white/5'
                      }`}
                      data-testid={`section-${section.section}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{section.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {selectedSection ? (
              <div className={`bg-gradient-to-br ${SECTION_COLORS[selectedSection.section] || 'from-gray-500/20 to-gray-600/20'} border border-white/10 rounded-xl overflow-hidden`}>
                {/* Section Header */}
                <div className="p-6 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {(() => {
                      const Icon = SECTION_ICONS[selectedSection.section] || Target;
                      return (
                        <div className="w-12 h-12 rounded-xl bg-[#C4A052]/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-[#C4A052]" />
                        </div>
                      );
                    })()}
                    <div>
                      <h2 className="text-2xl font-bold">{selectedSection.title}</h2>
                      <p className="text-gray-400 text-sm">
                        Last updated: {selectedSection.updated_at 
                          ? new Date(selectedSection.updated_at).toLocaleDateString() 
                          : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  {isAdmin && !editMode && (
                    <button
                      onClick={startEdit}
                      className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                      data-testid="edit-section-btn"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit
                    </button>
                  )}
                </div>

                {/* Section Content */}
                <div className="p-6">
                  {editMode ? (
                    <div className="space-y-4">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full h-96 bg-black/30 border border-white/10 rounded-lg p-4 text-sm font-mono focus:outline-none focus:border-[#C4A052] resize-none"
                        placeholder="Enter section content..."
                        data-testid="edit-content-textarea"
                      />
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setEditMode(false)}
                          className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveSection}
                          className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                          data-testid="save-section-btn"
                        >
                          <Check className="w-4 h-4" />
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed">
                      {renderContent(selectedSection.content)}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Target className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">Select a Section</h3>
                <p className="text-gray-500 text-sm">
                  Choose a blueprint section from the sidebar to view its content
                </p>
              </div>
            )}

            {/* Quick Overview Cards */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-black border border-white/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#C4A052]/20 flex items-center justify-center">
                    <Target className="w-4 h-4 text-[#C4A052]" />
                  </div>
                  <h4 className="font-medium text-sm">Brand Position</h4>
                </div>
                <p className="text-xs text-gray-400 line-clamp-2">
                  Leader, entrepreneur, and community builder using football as a platform to inspire elevation
                </p>
              </div>
              
              <div className="bg-black border border-white/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#C4A052]/20 flex items-center justify-center">
                    <Layers className="w-4 h-4 text-[#C4A052]" />
                  </div>
                  <h4 className="font-medium text-sm">6 Brand Pillars</h4>
                </div>
                <p className="text-xs text-gray-400 line-clamp-2">
                  Performance, Leadership, Entrepreneurship, Style, Community, Legacy
                </p>
              </div>
              
              <div className="bg-black border border-white/20 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-[#C4A052]/20 flex items-center justify-center">
                    <Palette className="w-4 h-4 text-[#C4A052]" />
                  </div>
                  <h4 className="font-medium text-sm">Visual Identity</h4>
                </div>
                <p className="text-xs text-gray-400 line-clamp-2">
                  Elevate Black, Steel, Elevate Gold — Anton, Montserrat, Inter typography
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AEBlueprint;
